import React, { useState, useEffect, useRef, useCallback } from 'react';
import { RefreshCw, Wifi, WifiOff, Bell, Settings, Filter, Search, SortAsc, SortDesc, Grid, List, CheckCircle, X, BookmarkCheck, FileText, RefreshCw as RefreshCwIcon, FileText as FileTextIcon } from 'lucide-react';
import { readerService, BreakingNewsArticle } from '../../services/readerService';

interface AutoRefreshSettings {
  enabled: boolean;
  interval: number; // seconds
  categories: string[];
  priority: string[];
  notifications: boolean;
  soundEnabled: boolean;
  vibrationEnabled: boolean;
  desktopNotifications: boolean;
  mobileNotifications: boolean;
  emailNotifications: boolean;
  pushNotifications: boolean;
  autoMarkAsRead: boolean;
  maxArticles: number;
  refreshOnFocus: boolean;
  refreshOnVisibility: boolean;
}

interface AutoRefreshProps {
  onArticleUpdate?: (article: BreakingNewsArticle) => void;
  onNotificationRequest?: (title: string, body: string) => void;
  onSettingsChange?: (settings: AutoRefreshSettings) => void;
}

const AutoRefresh: React.FC<AutoRefreshProps> = ({
  onArticleUpdate,
  onNotificationRequest,
  onSettingsChange
}) => {
  const [articles, setArticles] = useState<BreakingNewsArticle[]>([]);
  const [settings, setSettings] = useState<AutoRefreshSettings>({
    enabled: true,
    interval: 30,
    categories: ['breaking', 'urgent', 'update'],
    priority: ['high', 'critical'],
    notifications: true,
    soundEnabled: true,
    vibrationEnabled: true,
    desktopNotifications: true,
    mobileNotifications: true,
    emailNotifications: false,
    pushNotifications: true,
    autoMarkAsRead: false,
    maxArticles: 50,
    refreshOnFocus: true,
    refreshOnVisibility: true
  });

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showSettings, setShowSettings] = useState(false);
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [filterPriority, setFilterPriority] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState<'publishedAt' | 'updatedAt' | 'priority' | 'title'>('publishedAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const notificationPermissionRef = useRef<NotificationPermission | null>(null);

  useEffect(() => {
    loadInitialArticles();
    setupAutoRefresh();
    setupOnlineStatusListener();
    requestNotificationPermission();

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [settings.enabled, settings.interval]);

  useEffect(() => {
    onSettingsChange?.(settings);
  }, [settings, onSettingsChange]);

  const loadInitialArticles = async () => {
    try {
      // Load real breaking news from reader service
      const articles = await readerService.getBreakingNews(20);
      setArticles(articles);
      setUnreadCount(articles.filter(article => !article.isRead).length);
    } catch (error) {
      console.error('Failed to load articles:', error);
      // Fallback to empty array if error
      setArticles([]);
      setUnreadCount(0);
    }
  };

  const setupAutoRefresh = () => {
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    if (settings.enabled) {
      refreshIntervalRef.current = setInterval(() => {
        refreshArticles();
      }, settings.interval * 1000);
    }
  };

  const setupOnlineStatusListener = () => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window) {
      const permission = await Notification.requestPermission();
      notificationPermissionRef.current = permission;
    }
  };

  const refreshArticles = async () => {
    if (!isOnline) return;

    try {
      setIsRefreshing(true);
      
      // Load fresh breaking news from reader service
      const newArticles = await readerService.getBreakingNews(20);
      
      setArticles(prev => {
        // Merge new articles with existing ones, avoiding duplicates
        const existingIds = new Set(prev.map(article => article.id));
        const uniqueNewArticles = newArticles.filter(article => !existingIds.has(article.id));
        const updated = [...uniqueNewArticles, ...prev].slice(0, settings.maxArticles);
        setUnreadCount(updated.filter(article => !article.isRead).length);
        return updated;
      });

      setLastRefresh(new Date());
      
      // Send notification if there are new articles
      if (settings.notifications && notificationPermissionRef.current === 'granted' && newArticles.length > 0) {
        const latestArticle = newArticles[0];
        onNotificationRequest?.(latestArticle.title, latestArticle.summary);
        
        if (settings.desktopNotifications) {
          new Notification(latestArticle.title, {
            body: latestArticle.summary,
            icon: '/favicon.ico',
            tag: 'breaking-news'
          });
        }
      }

      onArticleUpdate?.(newArticles[0]);

    } catch (error) {
      console.error('Failed to refresh articles:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleManualRefresh = () => {
    refreshArticles();
  };

  const handleArticleRead = (articleId: string) => {
    setArticles(prev => 
      prev.map(article => 
        article.id === articleId 
          ? { ...article, isRead: true }
          : article
      )
    );
    setUnreadCount(prev => prev - 1);
  };

  const handleArticleBookmark = (articleId: string) => {
    setArticles(prev => 
      prev.map(article => 
        article.id === articleId 
          ? { ...article, isBookmarked: !article.isBookmarked }
          : article
      )
    );
  };

  const handleSettingsChange = (key: keyof AutoRefreshSettings, value: any) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'text-red-600 dark:text-red-400';
      case 'high': return 'text-orange-600 dark:text-orange-400';
      case 'medium': return 'text-yellow-600 dark:text-yellow-400';
      case 'low': return 'text-green-600 dark:text-green-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getPriorityBgColor = (priority: string) => {
    switch (priority) {
      case 'critical': return 'bg-red-100 dark:bg-red-900';
      case 'high': return 'bg-orange-100 dark:bg-orange-900';
      case 'medium': return 'bg-yellow-100 dark:bg-yellow-900';
      case 'low': return 'bg-green-100 dark:bg-green-900';
      default: return 'bg-gray-100 dark:bg-gray-800';
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'breaking': return 'text-red-600 dark:text-red-400';
      case 'urgent': return 'text-orange-600 dark:text-orange-400';
      case 'update': return 'text-blue-600 dark:text-blue-400';
      case 'developing': return 'text-purple-600 dark:text-purple-400';
      default: return 'text-gray-600 dark:text-gray-400';
    }
  };

  const formatDate = (date: Date): string => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const filteredArticles = articles
    .filter(article => {
      const matchesCategory = filterCategory === 'all' || article.category === filterCategory;
      const matchesPriority = filterPriority === 'all' || article.priority === filterPriority;
      const matchesSearch = article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.summary.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           article.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
      
      return matchesCategory && matchesPriority && matchesSearch;
    })
    .sort((a, b) => {
      let aValue: any, bValue: any;
      
      switch (sortBy) {
        case 'publishedAt':
          aValue = a.publishedAt.getTime();
          bValue = b.publishedAt.getTime();
          break;
        case 'updatedAt':
          aValue = a.updatedAt.getTime();
          bValue = b.updatedAt.getTime();
          break;
        case 'priority':
          const priorityOrder = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
          aValue = priorityOrder[a.priority as keyof typeof priorityOrder];
          bValue = priorityOrder[b.priority as keyof typeof priorityOrder];
          break;
        case 'title':
          aValue = a.title.toLowerCase();
          bValue = b.title.toLowerCase();
          break;
        default:
          return 0;
      }
      
      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              Breaking News Auto-Refresh
            </h1>
            <p className="text-gray-600 dark:text-gray-400">
              Live updates for breaking news and urgent articles
            </p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300">
              {isOnline ? <Wifi className="h-4 w-4" /> : <WifiOff className="h-4 w-4" />}
              <span className="text-sm font-medium">
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
            
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300">
              <Bell className="h-4 w-4" />
              <span className="text-sm font-medium">
                {unreadCount} Unread
              </span>
            </div>
            
            <button
              onClick={() => setShowSettings(!showSettings)}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              <Settings className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Status Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''} text-blue-600 dark:text-blue-400`} />
              <span className="text-sm text-gray-600 dark:text-gray-400">
                {isRefreshing ? 'Refreshing...' : 'Auto-refresh enabled'}
              </span>
            </div>
            
            <div className="text-sm text-gray-500 dark:text-gray-500">
              Last refresh: {formatDate(lastRefresh)}
            </div>
            
            <div className="text-sm text-gray-500 dark:text-gray-500">
              Next refresh in: {settings.interval}s
            </div>
          </div>
          
          <button
            onClick={handleManualRefresh}
            disabled={isRefreshing || !isOnline}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${isRefreshing ? 'animate-spin' : ''}`} />
            Refresh Now
          </button>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Categories</option>
              <option value="breaking">Breaking</option>
              <option value="urgent">Urgent</option>
              <option value="update">Update</option>
              <option value="developing">Developing</option>
            </select>

            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Priorities</option>
              <option value="critical">Critical</option>
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as any)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="publishedAt">Published</option>
              <option value="updatedAt">Updated</option>
              <option value="priority">Priority</option>
              <option value="title">Title</option>
            </select>

            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
            >
              {sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />}
            </button>
          </div>

          <div className="flex items-center gap-4">
            <div className="flex border border-gray-300 dark:border-gray-600 rounded-lg">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 ${viewMode === 'grid' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <Grid className="h-4 w-4" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 ${viewMode === 'list' ? 'bg-blue-500 text-white' : 'bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300'}`}
              >
                <List className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Articles List */}
      {viewMode === 'list' ? (
        <div className="space-y-4">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow ${
                !article.isRead ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityBgColor(article.priority)} ${getPriorityColor(article.priority)}`}>
                      {article.priority.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 ${getCategoryColor(article.category)}`}>
                      {article.category.toUpperCase()}
                    </span>
                    <span className="text-xs text-gray-500 dark:text-gray-500">
                      {formatDate(article.publishedAt)}
                    </span>
                  </div>
                  
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {article.title}
                  </h3>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-3">
                    {article.summary}
                  </p>
                  
                  <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-500">
                    <span>By {article.author}</span>
                    <span>•</span>
                    <span>{article.source}</span>
                    <span>•</span>
                    <span>{article.readTime} min read</span>
                    <span>•</span>
                    <span>{article.wordCount} words</span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleArticleRead(article.id)}
                    className={`p-2 rounded-lg ${
                      article.isRead 
                        ? 'text-green-600 hover:text-green-700' 
                        : 'text-gray-400 hover:text-green-600'
                    }`}
                    title={article.isRead ? 'Mark as unread' : 'Mark as read'}
                  >
                    <CheckCircle className="h-5 w-5" />
                  </button>
                  
                  <button
                    onClick={() => handleArticleBookmark(article.id)}
                    className={`p-2 rounded-lg ${
                      article.isBookmarked 
                        ? 'text-blue-600 hover:text-blue-700' 
                        : 'text-gray-400 hover:text-blue-600'
                    }`}
                    title={article.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                  >
                    <BookmarkCheck className="h-5 w-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredArticles.map((article) => (
            <div
              key={article.id}
              className={`bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm border border-gray-200 dark:border-gray-700 hover:shadow-md transition-shadow ${
                !article.isRead ? 'ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-900' : ''
              }`}
            >
              <div className="flex items-center gap-2 mb-3">
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${getPriorityBgColor(article.priority)} ${getPriorityColor(article.priority)}`}>
                  {article.priority.toUpperCase()}
                </span>
                <span className={`px-2 py-1 text-xs font-medium rounded-full bg-gray-100 dark:bg-gray-700 ${getCategoryColor(article.category)}`}>
                  {article.category.toUpperCase()}
                </span>
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                {article.title}
              </h3>
              
              <p className="text-gray-600 dark:text-gray-400 mb-4 text-sm">
                {article.summary}
              </p>
              
              <div className="text-xs text-gray-500 dark:text-gray-500 mb-4">
                <div>By {article.author}</div>
                <div>{article.source} • {formatDate(article.publishedAt)}</div>
                <div>{article.readTime} min read • {article.wordCount} words</div>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleArticleRead(article.id)}
                    className={`p-2 rounded-lg ${
                      article.isRead 
                        ? 'text-green-600 hover:text-green-700' 
                        : 'text-gray-400 hover:text-green-600'
                    }`}
                    title={article.isRead ? 'Mark as unread' : 'Mark as read'}
                  >
                    <CheckCircle className="h-4 w-4" />
                  </button>
                  
                  <button
                    onClick={() => handleArticleBookmark(article.id)}
                    className={`p-2 rounded-lg ${
                      article.isBookmarked 
                        ? 'text-blue-600 hover:text-blue-700' 
                        : 'text-gray-400 hover:text-blue-600'
                    }`}
                    title={article.isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
                  >
                    <BookmarkCheck className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filteredArticles.length === 0 && (
        <div className="text-center py-12">
          <Bell className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No articles found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Try adjusting your search or filter criteria
          </p>
        </div>
      )}

      {/* Settings Modal */}
      {showSettings && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                Auto-Refresh Settings
              </h2>
              <button
                onClick={() => setShowSettings(false)}
                className="p-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-6">
              {/* General Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  General Settings
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enable auto-refresh
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.enabled}
                      onChange={(e) => handleSettingsChange('enabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Refresh interval: {settings.interval} seconds
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="300"
                      step="10"
                      value={settings.interval}
                      onChange={(e) => handleSettingsChange('interval', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Maximum articles: {settings.maxArticles}
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="10"
                      value={settings.maxArticles}
                      onChange={(e) => handleSettingsChange('maxArticles', parseInt(e.target.value))}
                      className="w-full"
                    />
                  </div>
                </div>
              </div>

              {/* Notification Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Notification Settings
                </h3>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Enable notifications
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.notifications}
                      onChange={(e) => handleSettingsChange('notifications', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Desktop notifications
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.desktopNotifications}
                      onChange={(e) => handleSettingsChange('desktopNotifications', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Sound notifications
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.soundEnabled}
                      onChange={(e) => handleSettingsChange('soundEnabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      Vibration notifications
                    </label>
                    <input
                      type="checkbox"
                      checked={settings.vibrationEnabled}
                      onChange={(e) => handleSettingsChange('vibrationEnabled', e.target.checked)}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Filter Settings */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Filter Settings
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Categories to monitor
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['breaking', 'urgent', 'update', 'developing'].map(category => (
                        <label key={category} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.categories.includes(category)}
                            onChange={(e) => {
                              const newCategories = e.target.checked
                                ? [...settings.categories, category]
                                : settings.categories.filter(c => c !== category);
                              handleSettingsChange('categories', newCategories);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{category}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Priority levels to monitor
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      {['critical', 'high', 'medium', 'low'].map(priority => (
                        <label key={priority} className="flex items-center">
                          <input
                            type="checkbox"
                            checked={settings.priority.includes(priority)}
                            onChange={(e) => {
                              const newPriority = e.target.checked
                                ? [...settings.priority, priority]
                                : settings.priority.filter(p => p !== priority);
                              handleSettingsChange('priority', newPriority);
                            }}
                            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <span className="text-sm text-gray-700 dark:text-gray-300 capitalize">{priority}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-4 mt-6">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AutoRefresh;
